import {TrustStatus} from '@sollapay/enums';
import {useTranslation} from 'react-i18next';

import {getTrustAccountActionLabelKey} from '../../utils';
import {TrustAccountBalancePanel} from '../TrustAccountBalancePanel';
import {TrustAccountCardAction} from '../TrustAccountCardAction';
import {TrustAccountListItemMenu} from '../TrustAccountListItemMenu';
import {TrustAccountNameCell} from '../TrustAccountNameCell';
import {TrustAccountRoleBadge} from '../TrustAccountRoleBadge';
import {TrustAccountSellersCell} from '../TrustAccountSellersCell';
import {TrustAccountTeamCell} from '../TrustAccountTeamCell';
import {TrustAccountTypeCell} from '../TrustAccountTypeCell';
import {TrustAccountGridCard} from './TrustAccountGridCard';

import type {MemberTrustAccountListHandlers, MemberTrustAccountListItem} from '../../utils';
import type {FC} from 'react';

export interface TrustAccountMemberCardProps extends MemberTrustAccountListHandlers {
  item: MemberTrustAccountListItem;
  showRoleBadge?: boolean;
}

/** An account in setup gets no balance panel and no seller/buyers row, so active cards draw the eye. */
export const TrustAccountMemberCard: FC<TrustAccountMemberCardProps> = ({
  item,
  showRoleBadge = false,
  onEditAccount,
  onDeleteAccount,
  onAccountAction,
  onManageTeam
}) => {
  const {t} = useTranslation('common');
  const isActive = item.trustStatus === TrustStatus.TRUST_ACTIVE;

  return (
    <TrustAccountGridCard
      balance={
        isActive && <TrustAccountBalancePanel status={item.trustStatus} balance={item.balance} />
      }
      identity={
        <TrustAccountNameCell
          variant="card"
          name={item.name}
          refId={item.refId}
          trustPurpose={item.trustPurpose}
          rowLinkTo={item.path}
          roleBadge={showRoleBadge ? <TrustAccountRoleBadge accessRole={item.role} /> : undefined}
        />
      }
      menu={
        <TrustAccountListItemMenu
          item={item}
          onEditAccount={onEditAccount}
          onDeleteAccount={onDeleteAccount}
        />
      }
      meta={
        isActive && (
          <TrustAccountSellersCell
            layout="inline"
            sellers={item.sellers}
            buyersCount={item.buyersCount}
            onOpenBuyers={() => onAccountAction?.(item)}
          />
        )
      }
      footerStart={
        <TrustAccountTeamCell
          trustee={item.trustee}
          team={item.team}
          onManageTeam={() => onManageTeam?.(item)}
        />
      }
      footerEnd={
        <TrustAccountCardAction
          rest={
            <TrustAccountTypeCell
              layout="inline"
              trustPurpose={item.trustPurpose}
              generalSaleTransactionType={item.generalSaleTransactionType}
            />
          }
          ctaLabel={t(getTrustAccountActionLabelKey(item.trustStatus, item.permissions.canManage))}
          onAction={() => onAccountAction?.(item)}
        />
      }
    />
  );
};
